import apiClient from './client';
import { API_ENDPOINTS } from './config';
import type { BookingLinkTokens } from '../types/bookingLink';

const PUBLIC_BOOKING_PATH = '/book';

/**
 * Origin the portal itself is served from — and therefore where the public
 * booking pages live.
 *
 * `VITE_PORTAL_BASE_URL` pins it per environment (test./www. portal domains).
 * Left unset in local development it falls back to the origin the page is
 * already running on, so a generated link opens in the same browser whatever
 * port Vite bound to. Read live rather than at import time for that reason.
 * Setting it locally is still useful to pin a LAN address for phone testing.
 */
const getPortalOrigin = (): string => {
	const configured = import.meta.env.VITE_PORTAL_BASE_URL;
	if (configured) return configured;
	return typeof window !== 'undefined' ? window.location.origin : '';
};

const withToken = (origin: string, token: string): string =>
	`${origin.replace(/\/+$/, '')}${PUBLIC_BOOKING_PATH}?token=${encodeURIComponent(token)}`;

export const bookingLinkApi = {
	/**
	 * Get the public booking tokens for a location.
	 */
	generate: async (locationId: number): Promise<BookingLinkTokens> => {
		return apiClient.post<BookingLinkTokens>(
			`${API_ENDPOINTS.BOOKING_LINK.GENERATE}?locationId=${locationId}`
		);
	},
};

/**
 * Shareable customer booking link for a token.
 *
 * Both kinds of link — with payment and without — are served from the portal
 * domain, since the token itself decides which page renders. The paid flow was
 * briefly pointed at the API host while it was still being built; now that it
 * ships, there is one builder and one origin.
 */
export const buildBookingUrl = (token: string): string => withToken(getPortalOrigin(), token);
